import type { SurveyListItem } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import type { ColumnDirection } from 'packages/hotsauce/build';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';

export type SortKeys = keyof SurveyListItem;

export interface SortState {
  key: SortKeys;
  direction: ColumnDirection;
}

function compare<T extends string | number | boolean | null>(aValue: T, bValue: T) {
  if (aValue === null || bValue === null) {
    if (aValue === null && bValue !== null) return -1;
    if (aValue !== null && bValue === null) return 1;
    return 0;
  }

  if (aValue < bValue) return -1;
  if (aValue > bValue) return 1;

  return 0;
}

function compareSurveys(key: SortKeys, direction: ColumnDirection) {
  const compareByKeyThenDate = (a: SurveyListItem, b: SurveyListItem) => {
    const result = compare(a[key], b[key]);

    if (result !== 0) {
      return result;
    }

    return compare(a.created_epoch_time, b.created_epoch_time);
  };

  const sortDirection = (result: -1 | 1 | 0) => {
    if (direction !== 'asc') {
      return result === 1 ? -1 : 1;
    }
    return result;
  };

  return (a: SurveyListItem, b: SurveyListItem) => sortDirection(compareByKeyThenDate(a, b));
}

export type SurveySorting = {
  sortState: SortState;
  setSortState: Dispatch<SetStateAction<SortState>>;
  onSortChanged: (key: SortKeys, direction: ColumnDirection) => void;
  surveyOrder: number[] | undefined;
  setSurveyOrder: Dispatch<SetStateAction<number[] | undefined>>;
};

export const useSurveysListSorting = (surveys?: SurveyListItem[]): SurveySorting => {
  const [sortState, setSortState] = useState<SortState>({ key: 'enabled', direction: 'desc' });
  const [surveyOrder, setSurveyOrder] = useState<number[]>();

  useEffect(() => {
    if (!surveyOrder && surveys) {
      setSurveyOrder(surveys.map(({ id }) => id));
    }
  }, [surveys, surveyOrder, setSurveyOrder]);

  const onSortChanged = useCallback(
    (key: SortKeys, direction: ColumnDirection) => {
      setSortState({ key, direction });
      if (surveys) {
        setSurveyOrder([...surveys].sort(compareSurveys(key, direction)).map(({ id }) => id));
      }
    },
    [surveys],
  );

  return { sortState, setSortState, onSortChanged, surveyOrder, setSurveyOrder };
};
